import React, {useEffect, useState, useRef} from 'react';
import {Badge, Button, Container, Modal, ModalBody, ModalHeader} from 'reactstrap';
import {useDispatch, useSelector} from 'react-redux';
import {NavLink} from 'react-router-dom';
import MovieSlider from '../../components/MovieSlider';
import {fetchHomefeed, setHomepageScrollY, setHomepageSearchQuery} from '../../slices/movies.slice';

import './Homepage.scss';
import YoutubeTrailer from '../../components/YoutubeTrailer';
import {imgUrl} from '../../utils/api-request';
import Loading from '../../components/Loading';

// previewOrganizationId renders the homepage as a given organization's users
// would see it (BAC staff preview). It scopes the feed requests to that
// organization and disables the personal touches: the fixed search bar and
// scroll/search persistence.
const SiteHomepage = ({ previewOrganizationId }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const [modalTrailer, setModalTrailer] = useState(false);
    const persistedSearchQuery = useSelector(state => state.movies.homepageSearchQuery);
    const [searchQuery, setSearchQueryLocal] = useState(previewOrganizationId ? '' : persistedSearchQuery);
    const [searchVisible, setSearchVisible] = useState(!!persistedSearchQuery && !previewOrganizationId);
    const setSearchQuery = (value) => {
        setSearchQueryLocal(value);
        dispatch(setHomepageSearchQuery(value));
    };
    const [contentVisible, setContentVisible] = useState(false);
    const pendingImages = useRef(0);
    const revealTimeout = useRef(null);
    const scrollRestored = useRef(false);

    const {
        data: highlight,
        status: highlightStatus,
    } = useSelector(state => state.movies.homefeeds['highlight']);

    const comingSoonFeed = useSelector(state => state.movies.homefeeds['coming-soon']);
    const nowShowingFeed = useSelector(state => state.movies.homefeeds['now-showing']);
    const purchasesFeed = useSelector(state => state.movies.homefeeds['purchases']);
    const homepageScrollY = useSelector(state => state.movies.homepageScrollY);

    const initialStatuses = useRef({
        highlight: highlightStatus,
        comingSoon: comingSoonFeed.status,
        nowShowing: nowShowingFeed.status,
        purchases: purchasesFeed.status,
    });

    const toggleTrailer = () => {setModalTrailer(!modalTrailer)};

    // Filter versions based on search query
    const filterVersions = (versions) => {
        if (!searchQuery.trim()) return versions;
        return versions.filter(({ movie, version }) =>
            movie.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            version.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    // Check if all data feeds are loaded
    const allDataLoaded =
        highlightStatus === 'success' &&
        comingSoonFeed.status === 'success' &&
        nowShowingFeed.status === 'success' &&
        purchasesFeed.status === 'success';

    // Count images and start timeout when data is ready.
    // Dependencies intentionally exclude the data objects — we only want this to fire once
    // when allDataLoaded first becomes true. Including data in deps caused cleanup to cancel
    // the timeout on every background refresh, leaving the page permanently invisible.
    useEffect(() => {
        if (!allDataLoaded) return;

        let count = 0;
        if (highlight?.movie?.still?.imageUrl) count++;
        if (highlight?.movie?.logo?.imageUrl) count++;
        [comingSoonFeed.data, nowShowingFeed.data, purchasesFeed.data].forEach(feed => {
            feed?.forEach(item => {
                if (item.movie?.poster?.imageUrl) count++;
            });
        });

        pendingImages.current = count;

        if (count === 0) {
            setContentVisible(true);
            return;
        }

        revealTimeout.current = setTimeout(() => {
            setContentVisible(true);
        }, 5000);

        return () => {
            if (revealTimeout.current) clearTimeout(revealTimeout.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allDataLoaded]);

    // Called by each image when it loads
    const handleImageLoad = () => {
        pendingImages.current--;
        if (pendingImages.current <= 0) {
            if (revealTimeout.current) clearTimeout(revealTimeout.current);
            setContentVisible(true);
        }
    };

    useEffect(() => {
        const s = initialStatuses.current;
        // In preview mode never fetch silently: the store may hold another
        // organization's feed, so show Loading until this one arrives.
        const organizationId = previewOrganizationId;
        dispatch(fetchHomefeed({ type: 'highlight', silent: !organizationId && s.highlight === 'success', organizationId }));
        dispatch(fetchHomefeed({ type: 'coming-soon', silent: !organizationId && s.comingSoon === 'success', organizationId }));
        dispatch(fetchHomefeed({ type: 'now-showing', silent: !organizationId && s.nowShowing === 'success', organizationId }));
        dispatch(fetchHomefeed({ type: 'purchases', silent: !organizationId && s.purchases === 'success', organizationId }));
    }, [dispatch, previewOrganizationId]);

    // Silent background refresh every 10 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            dispatch(fetchHomefeed({ type: 'highlight', silent: true, organizationId: previewOrganizationId }));
            dispatch(fetchHomefeed({ type: 'coming-soon', silent: true, organizationId: previewOrganizationId }));
            dispatch(fetchHomefeed({ type: 'now-showing', silent: true, organizationId: previewOrganizationId }));
            dispatch(fetchHomefeed({ type: 'purchases', silent: true, organizationId: previewOrganizationId }));
        }, 10000);

        return () => clearInterval(interval);
    }, [dispatch, previewOrganizationId]);

    // Restore page scroll position after content is visible
    useEffect(() => {
        if (!contentVisible || previewOrganizationId) return;
        requestAnimationFrame(() => {
            const container = document.querySelector('.main-content');
            if (container && homepageScrollY) {
                container.scrollTop = homepageScrollY;
            }
            scrollRestored.current = true;
        });
    }, [contentVisible, homepageScrollY, previewOrganizationId]);

    // Save page scroll position on scroll (debounced)
    useEffect(() => {
        if (previewOrganizationId) return;
        const container = document.querySelector('.main-content');
        if (!container) return;
        let timeout;
        const handleScroll = () => {
            if (!scrollRestored.current) return;
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                dispatch(setHomepageScrollY(container.scrollTop));
            }, 100);
        };
        container.addEventListener('scroll', handleScroll);
        return () => {
            clearTimeout(timeout);
            container.removeEventListener('scroll', handleScroll);
        };
    }, [dispatch, previewOrganizationId]);

    if (!user) {
        return <Loading fullHeight />;
    }

    if (!allDataLoaded) {
        return <Loading fullHeight />;
    }

    return (
        <div
            className="bg-black min-vh-100 position-relative"
            style={{
                opacity: contentVisible ? 1 : 0,
                transition: 'opacity 0.3s ease-out'
            }}
        >
            {/* Search Bar — fixed to the viewport, so hidden in preview mode */}
            {!previewOrganizationId && (
            <div className="search-bar-container">
                {!searchVisible ? (
                    <button
                        className="search-icon-button"
                        onClick={() => setSearchVisible(true)}
                        title="Search movies"
                        aria-label="Open search"
                    >
                        <span className="fas fa-search"/>
                    </button>
                ) : (
                    <div className="search-bar-expanded">
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search movies..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                            aria-label="Search movies and versions"
                        />
                        <button
                            className="search-close-button"
                            onClick={() => {
                                setSearchQuery('');
                                setSearchVisible(false);
                            }}
                            title="Close search"
                            aria-label="Close search"
                        >
                            <span className="fas fa-times"/>
                        </button>
                    </div>
                )}
            </div>
            )}

            {highlight && !searchQuery.trim() && (
                <div className="highlight-film pb-3">
                    {/* Hidden img to track background-image load */}
                    {highlight.movie.still?.imageUrl && (
                        <img
                            src={imgUrl(highlight.movie.still.imageUrl)}
                            onLoad={handleImageLoad}
                            onError={handleImageLoad}
                            style={{ display: 'none' }}
                            alt=""
                        />
                    )}
                    <div className="film-cover d-flex flex-column"
                         style={{backgroundImage: `url("${imgUrl(highlight.movie.still?.imageUrl)}")`}}>
                        <div className="gradient w-100 mt-auto"></div>
                    </div>
                    <div className="film-actions ps-3">
                        <div className="film-logo">
                            {highlight.movie.logo?.imageUrl ? (
                                <img alt="" draggable="false"
                                     src={imgUrl(highlight.movie.logo.imageUrl)}
                                     className="img-fluid"
                                     onLoad={handleImageLoad}
                                     onError={handleImageLoad}/>
                            ) : (
                                <h1 className="text-white fw-bold mb-0"
                                    style={{textShadow: '0 2px 12px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.8)'}}>
                                    {highlight.movie.title}
                                </h1>
                            )}
                        </div>

                        <div className="restrictions-genres-box mt-2">
                            {highlight.contentRestrictions && (
                                <Badge color="transparent"
                                       className="border border-white me-2">{highlight.contentRestrictions}+</Badge>
                            )}
                            {highlight.movie.genres.map(genre => (
                                <Badge key={genre} color="transparent" className="text-uppercase">{genre}</Badge>
                            ))}
                        </div>

                        <p className="movie-synopsis lh-2 mt-2">
                            {highlight.movie.synopsis}
                        </p>

                        <div className="d-flex gap-2 flex-wrap">
                            <NavLink to={highlight.orderId
                                ? `/movie-version/${highlight._id}/order/${highlight.orderId}`
                                : `/movie-version/${highlight._id}`}>
                                <Button color="primary">
                                    <span className="fas fa-video-camera me-2"/>
                                    View details
                                </Button>
                            </NavLink>

                            {highlight.movie.youtubeTrailerId && (
                                <Button color="primary" outline onClick={toggleTrailer}>
                                    <span className="fas fa-play me-2"/>
                                    Play trailer
                                </Button>
                            )}
                        </div>
                    </div>

                    <Modal isOpen={modalTrailer} toggle={toggleTrailer} fullscreen modalClassName="trailer">
                        <ModalHeader tag="div" toggle={toggleTrailer} className="non-editable"/>
                        <ModalBody>
                            <YoutubeTrailer
                                videoId={highlight.movie.youtubeTrailerId}
                                title={highlight.movie.title}
                                value={highlight.movie.youtubeTrailerId}
                            />
                        </ModalBody>
                    </Modal>
                </div>
            )}

            <Container fluid>
                {!!filterVersions(comingSoonFeed.data || []).length && (
                    <MovieSlider group="Coming Soon" versions={filterVersions(comingSoonFeed.data)} onImageLoad={handleImageLoad} scrollKey="coming-soon" />
                )}

                {!!filterVersions(nowShowingFeed.data || []).length && (
                    <MovieSlider group="Now Showing" versions={filterVersions(nowShowingFeed.data)} onImageLoad={handleImageLoad} scrollKey="now-showing" />
                )}

                {!!filterVersions(purchasesFeed.data || []).length && (
                    <MovieSlider group="My orders" versions={filterVersions(purchasesFeed.data)} onImageLoad={handleImageLoad} scrollKey="purchases" />
                )}

                {searchQuery.trim() &&
                    !filterVersions(comingSoonFeed.data || []).length &&
                    !filterVersions(nowShowingFeed.data || []).length &&
                    !filterVersions(purchasesFeed.data || []).length && (
                    <div className="text-white text-center py-5">
                        No results found for "{searchQuery}"
                    </div>
                )}

                <div className="p-5 mt-5" />
            </Container>
        </div>
    );
};

export default SiteHomepage;
